import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { TELEPHONY_PLUGIN_CONFIG, TelephonyPluginConfig } from './telephony-plugin.config';
import { environment } from '../../../environments/environment';

const CONFIG_KEY = 'telephony-plugin-profile';
const TOKEN_KEY = 'auth-token';
const CONFIG_URL = `${environment.apiHost}/api/v1/telephony/config`;

@Injectable({ providedIn: 'root' })
export class TelephonyPluginConfigService {
  private readonly configSubject = new BehaviorSubject<TelephonyPluginConfig>(this.load());
  readonly config$ = this.configSubject.asObservable();

  constructor() {
    void this.loadRemote();
  }

  get value(): TelephonyPluginConfig {
    return this.configSubject.value;
  }

  async save(config: TelephonyPluginConfig): Promise<void> {
    const merged = this.mergeWithDefaults(config);
    await this.saveRemote(merged);
  }

  async reset(): Promise<void> {
    localStorage.removeItem(CONFIG_KEY);
    const defaults = this.cloneDefault();
    this.configSubject.next(defaults);
    await this.saveRemote(defaults);
  }

  async loadRemote(): Promise<void> {
    try {
      const token = localStorage.getItem(TOKEN_KEY) ?? '';
      const res = await fetch(CONFIG_URL, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) return;
      const json: any = await res.json();
      const remote = this.mergeWithDefaults(json?.data ?? json);
      localStorage.setItem(CONFIG_KEY, JSON.stringify(remote));
      this.configSubject.next(remote);
    } catch {}
  }

  async testLookup(phone: string): Promise<any> {
    const token = localStorage.getItem(TOKEN_KEY) ?? '';
    const res = await fetch(`${CONFIG_URL}/test-lookup/${encodeURIComponent(phone)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json: any = await res.json();
    return json?.data ?? json;
  }

  async health(): Promise<any> {
    const token = localStorage.getItem(TOKEN_KEY) ?? '';
    const res = await fetch(`${CONFIG_URL}/health`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json: any = await res.json();
    return json?.data ?? json;
  }

  private load(): TelephonyPluginConfig {
    try {
      const saved = localStorage.getItem(CONFIG_KEY);
      if (saved) return this.mergeWithDefaults(JSON.parse(saved));
    } catch {}
    return this.cloneDefault();
  }

  private mergeWithDefaults(config: Partial<TelephonyPluginConfig>): TelephonyPluginConfig {
    const defaults = this.cloneDefault();
    return {
      ...defaults,
      ...config,
      labels: { ...defaults.labels, ...config.labels },
      roles: { ...defaults.roles, ...config.roles },
      adapter: { ...defaults.adapter, ...config.adapter },
      wrapUpOptions: { ...defaults.wrapUpOptions, ...config.wrapUpOptions },
      sla: { ...defaults.sla, ...config.sla },
      queueMapping:  Array.isArray(config.queueMapping)  ? config.queueMapping  : defaults.queueMapping,
      ivrMenus:      Array.isArray(config.ivrMenus)      ? config.ivrMenus      : defaults.ivrMenus,
      didRouting:    Array.isArray(config.didRouting)     ? config.didRouting    : defaults.didRouting,
      businessHours: config.businessHours ?? defaults.businessHours,
      storageKeys: { ...defaults.storageKeys, ...config.storageKeys },
      menu: defaults.menu,
    };
  }

  private cloneDefault(): TelephonyPluginConfig {
    return JSON.parse(JSON.stringify(TELEPHONY_PLUGIN_CONFIG));
  }

  private async saveRemote(config: TelephonyPluginConfig): Promise<void> {
    try {
      const token = localStorage.getItem(TOKEN_KEY) ?? '';
      const res = await fetch(CONFIG_URL, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(this.toRemotePayload(config)),
      });
      if (!res.ok) {
        const json: any = await res.json().catch(() => ({}));
        const errors = json?.data?.errors;
        const err = new Error(Array.isArray(errors) && errors.length ? errors.join('\n') : `HTTP ${res.status}`);
        (err as any).remoteValidation = true;
        throw err;
      }
      const json: any = await res.json();
      const saved = this.mergeWithDefaults(json?.data ?? json);
      localStorage.setItem(CONFIG_KEY, JSON.stringify(saved));
      this.configSubject.next(saved);
    } catch (e) {
      if ((e as any)?.remoteValidation) throw e;
      localStorage.setItem(CONFIG_KEY, JSON.stringify(config));
      this.configSubject.next(config);
    }
  }

  private toRemotePayload(config: TelephonyPluginConfig) {
    return {
      pluginId: config.pluginId,
      hostSystem: config.hostSystem,
      mountPath: config.mountPath,
      labels: config.labels,
      roles: config.roles,
      adapter: config.adapter,
      wrapUpOptions: config.wrapUpOptions,
      sla: config.sla,
      queueMapping:  config.queueMapping,
      ivrMenus:      config.ivrMenus,
      didRouting:    config.didRouting,
      businessHours: config.businessHours,
    };
  }
}
